import { useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEventPeople, fetchMediaOcr, fetchMediaWithOcr } from '../api/ocr';
import { isMarchandViveretHost, readBearerToken, subscribeToBearerToken } from '../grampsAuth';

/** OCR transcription of one media, or `undefined` while it loads or without a handle. */
export function useMediaOcr(treeName: string | undefined, mediaHandle: string | null | undefined) {
    const isMarchand = isMarchandViveretHost();
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    return useQuery({
        queryKey: ['ocr', 'media', treeName, mediaHandle],
        queryFn: () => fetchMediaOcr(mediaHandle as string, token),
        enabled: Boolean(mediaHandle && (isMarchand || token)),
        // Transcriptions change at most once a night.
        staleTime: 10 * 60 * 1000,
        retry: 1,
    });
}

/** Set of the given media handles that have a transcription, in one request. */
export function useMediaWithOcr(treeName: string | undefined, mediaHandles: string[]) {
    const isMarchand = isMarchandViveretHost();
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    const sorted = [...mediaHandles].sort();
    const query = useQuery({
        queryKey: ['ocr', 'media-with-ocr', treeName, sorted.join(',')],
        queryFn: () => fetchMediaWithOcr(sorted, token),
        enabled: sorted.length > 0 && Boolean(isMarchand || token),
        staleTime: 10 * 60 * 1000,
        retry: false,
    });
    return new Set(query.data ?? []);
}

/** The people an event's acts name as witness, informant or godparent. Only
 * asked for events that are recorded in an act. */
export function useEventPeople(treeName: string | null | undefined, eventHandle: string | undefined, enabled: boolean) {
    const isMarchand = isMarchandViveretHost();
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    const query = useQuery({
        queryKey: ['ocr', 'event-people', treeName, eventHandle],
        queryFn: () => fetchEventPeople(eventHandle as string, token),
        enabled: enabled && Boolean(eventHandle && (isMarchand || token)),
        // The links machine runs at most a few times a day.
        staleTime: 10 * 60 * 1000,
        retry: false,
    });
    return query.data ?? [];
}
